import { resolveMediaUrl } from './mediaUrl';
import { isFitLiveUiEnabled } from './fitLiveModule';

const FAVICON_ATTR = 'data-ecosistema-brand-favicon';
const AUTH_TENANT_KEY = 'auth_tenant_brand';
const FAVICON_SIZE = 64;
const DEFAULT_ICON_HREF = '/favicon-ecosistema-fit.png';
const PLATFORM_TAB_TITLE = 'Ecosistema Fit';

function isPlatformD28dLabel(name) {
  const n = String(name || '').trim().toLowerCase();
  if (!n) return true;
  return /\bd28d\b/.test(n) || /gym\s*virtual/.test(n) || /gimnasio\s*virtual/.test(n);
}

export function platformTabTitle(name) {
  if (isFitLiveUiEnabled() && isPlatformD28dLabel(name)) return PLATFORM_TAB_TITLE;
  const t = String(name || '').trim();
  return t || PLATFORM_TAB_TITLE;
}

let applySeq = 0;

/** URL de icono de pestaña: favicon_url o, si no hay, logo_url. */
export function resolveBrandFaviconUrl(branding) {
  if (!branding || typeof branding !== 'object') return '';
  const raw = String(
    branding.favicon_url
    || branding.faviconUrl
    || branding.logo_url
    || branding.logoUrl
    || '',
  ).trim();
  if (!raw) return '';
  return resolveMediaUrl(raw) || raw;
}

/** Path same-origin para fetch/proxy Vite (/uploads → :3002). */
export function faviconSourceUrl(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';
  if (raw.startsWith('data:')) return raw;
  try {
    if (typeof window !== 'undefined' && /^https?:\/\//i.test(raw)) {
      const u = new URL(raw);
      if (u.origin === window.location.origin) {
        return `${u.pathname}${u.search || ''}`;
      }
    }
  } catch { /* keep */ }
  if (raw.startsWith('/')) return raw;
  return resolveMediaUrl(raw) || raw;
}

/**
 * Rasteriza logo (aunque sea banner ancho) a PNG 64×64.
 * Usa fetch+blob+createImageBitmap para evitar canvas “tainted” por crossOrigin.
 */
export async function rasterizeSquareFavicon(srcUrl) {
  if (typeof document === 'undefined') {
    throw new Error('no document');
  }
  const src = faviconSourceUrl(srcUrl);
  if (!src) throw new Error('empty src');
  if (src.startsWith('data:image/')) return src;

  const res = await fetch(src, { credentials: 'same-origin', cache: 'force-cache' });
  if (!res.ok) throw new Error(`favicon fetch ${res.status}`);
  const blob = await res.blob();
  if (!blob || !String(blob.type || '').startsWith('image/')) {
    // algunos servers no mandan content-type; igual intentamos
    if (!blob?.size) throw new Error('empty blob');
  }

  let bitmap;
  if (typeof createImageBitmap === 'function') {
    bitmap = await createImageBitmap(blob);
  } else {
    const objectUrl = URL.createObjectURL(blob);
    try {
      bitmap = await new Promise((resolve, reject) => {
        const img = new Image();
        img.onload = () => resolve(img);
        img.onerror = () => reject(new Error('image load failed'));
        img.src = objectUrl;
      });
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  }

  const canvas = document.createElement('canvas');
  canvas.width = FAVICON_SIZE;
  canvas.height = FAVICON_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('no canvas');
  ctx.clearRect(0, 0, FAVICON_SIZE, FAVICON_SIZE);
  const bw = bitmap.width || bitmap.naturalWidth || FAVICON_SIZE;
  const bh = bitmap.height || bitmap.naturalHeight || FAVICON_SIZE;
  const scale = Math.min(FAVICON_SIZE / bw, FAVICON_SIZE / bh);
  const w = Math.max(1, bw * scale);
  const h = Math.max(1, bh * scale);
  ctx.drawImage(bitmap, (FAVICON_SIZE - w) / 2, (FAVICON_SIZE - h) / 2, w, h);
  if (typeof bitmap.close === 'function') bitmap.close();
  return canvas.toDataURL('image/png');
}

function iconLinkSelector() {
  return 'link[rel="icon"], link[rel="shortcut icon"]';
}

function ensureLink(rel, attrs = {}) {
  let link = document.querySelector(`link[rel="${rel}"]`);
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', rel);
    document.head.appendChild(link);
  }
  Object.entries(attrs).forEach(([k, v]) => {
    if (v == null || v === '') return;
    link.setAttribute(k, String(v));
  });
  return link;
}

function ensureMeta(name, content) {
  if (!content) return;
  let meta = document.querySelector(`meta[name="${name}"]`);
  if (!meta) {
    meta = document.createElement('meta');
    meta.setAttribute('name', name);
    document.head.appendChild(meta);
  }
  meta.setAttribute('content', String(content));
}

function toAbsoluteUrl(href) {
  const raw = String(href || '').trim();
  if (!raw) return '';
  if (raw.startsWith('data:') || /^https?:\/\//i.test(raw)) return raw;
  try {
    if (typeof window !== 'undefined') {
      return new URL(raw, window.location.origin).href;
    }
  } catch { /* noop */ }
  return raw;
}

/**
 * Icono same-origin para acceso rápido móvil (iOS/Android).
 * Usa /api/gyms/public/by-slug/:slug/icon cuando hay slug.
 */
export function resolveHomeScreenIconHref(branding) {
  const slug = String(branding?.brand_slug || '').trim();
  if (slug && typeof window !== 'undefined') {
    return `${window.location.origin}/api/gyms/public/by-slug/${encodeURIComponent(slug)}/icon`;
  }
  const fav = resolveBrandFaviconUrl(branding);
  return fav ? toAbsoluteUrl(faviconSourceUrl(fav) || fav) : '';
}

/**
 * Sobrescribe apple-touch-icon + nombre de app (acceso rápido).
 * No usa data: URL (iOS lo ignora); prefiere icono HTTP same-origin.
 */
export function applyHomeScreenBrand(branding) {
  if (typeof document === 'undefined' || !branding) return;
  const name = String(branding.brand_name || branding.nombre || '').trim();
  const iconHref = resolveHomeScreenIconHref(branding);
  if (iconHref) {
    // URL estable (sin ?v=): iOS cachea mal si el href cambia en cada carga.
    const apple = ensureLink('apple-touch-icon', {
      [FAVICON_ATTR]: '1',
      sizes: '180x180',
    });
    apple.setAttribute('href', '');
    apple.setAttribute('href', iconHref);
  }
  if (name) {
    ensureMeta('application-name', platformTabTitle(name));
    ensureMeta('apple-mobile-web-app-title', platformTabTitle(name));
  }
}

export function resetHomeScreenBrand() {
  if (typeof document === 'undefined') return;
  const apple = document.querySelector('link[rel="apple-touch-icon"]');
  if (apple) {
    apple.setAttribute('href', `/apple-touch-icon.png?v=${Date.now()}`);
    apple.removeAttribute(FAVICON_ATTR);
  }
  ensureMeta('application-name', PLATFORM_TAB_TITLE);
  ensureMeta('apple-mobile-web-app-title', PLATFORM_TAB_TITLE);
}

/**
 * Sobrescribe TODOS los link de icono existentes (incluye D28D del index.html).
 * No borra/restaura: evita que StrictMode deje otra vez el favicon D28D.
 */
function setAllFaviconHrefs(href) {
  const links = Array.from(document.querySelectorAll(iconLinkSelector()));
  if (links.length === 0) {
    const icon = document.createElement('link');
    icon.setAttribute('rel', 'icon');
    icon.setAttribute(FAVICON_ATTR, '1');
    icon.setAttribute('type', 'image/png');
    icon.setAttribute('href', href);
    document.head.appendChild(icon);
    const shortcut = document.createElement('link');
    shortcut.setAttribute('rel', 'shortcut icon');
    shortcut.setAttribute(FAVICON_ATTR, '1');
    shortcut.setAttribute('type', 'image/png');
    shortcut.setAttribute('href', href);
    document.head.appendChild(shortcut);
    return;
  }
  links.forEach((link) => {
    link.setAttribute(FAVICON_ATTR, '1');
    link.setAttribute('type', 'image/png');
    // Forzar cambio de href aunque sea el mismo (Chrome cache).
    link.setAttribute('href', '');
    link.setAttribute('href', href);
  });
}

/**
 * Aplica favicon de marca.
 * Cleanup de React NO restaura D28D (eso rompía el icono con StrictMode).
 * Solo `resetDocumentFavicon()` vuelve al default (logout).
 */
export function applyDocumentFavicon(url, branding = null) {
  if (typeof document === 'undefined') return () => {};
  const src = faviconSourceUrl(url);
  if (!src && !branding) return () => {};

  const seq = ++applySeq;

  // Acceso rápido móvil: apple-touch-icon same-origin (antes de raster data:).
  if (branding) {
    applyHomeScreenBrand(branding);
  }

  if (src) {
    // Aplicación inmediata (path o data URL) para no esperar al raster.
    if (src.startsWith('data:')) {
      setAllFaviconHrefs(src);
    } else {
      const bust = src.includes('?') ? `${src}&v=${Date.now()}` : `${src}?v=${Date.now()}`;
      setAllFaviconHrefs(bust);
    }

    if (!src.startsWith('data:')) {
      rasterizeSquareFavicon(src)
        .then((dataUrl) => {
          if (seq !== applySeq) return;
          setAllFaviconHrefs(dataUrl);
        })
        .catch(() => { /* dejar href directo */ });
    }
  }

  // Cleanup intencionalmente vacío: no devolver D28D en StrictMode remount.
  return () => {};
}

/** Restaura favicon de plataforma (usar en logout). */
export function resetDocumentFavicon() {
  applySeq += 1;
  if (typeof document === 'undefined') return;
  setAllFaviconHrefs(`${DEFAULT_ICON_HREF}?v=${Date.now()}`);
  resetHomeScreenBrand();
}

export function applyDocumentTitle(name) {
  if (typeof document === 'undefined') return () => {};
  const next = platformTabTitle(name);
  if (!next) return () => {};
  document.title = next;
  return () => {};
}

/** Lee/guarda branding de sede|gym|coach para el login pre-auth. */
export function getAuthTenantBrand() {
  try {
    const raw = sessionStorage.getItem(AUTH_TENANT_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

export function setAuthTenantBrand(branding) {
  try {
    if (!branding || typeof branding !== 'object') {
      sessionStorage.removeItem(AUTH_TENANT_KEY);
      return;
    }
    sessionStorage.setItem(AUTH_TENANT_KEY, JSON.stringify(branding));
  } catch { /* noop */ }
}

export function clearAuthTenantBrand() {
  try { sessionStorage.removeItem(AUTH_TENANT_KEY); } catch { /* noop */ }
}

export function pickLoginHeroFromBranding(branding) {
  const links = branding?.social_links && typeof branding.social_links === 'object'
    ? branding.social_links
    : {};
  const hero = links.login_hero && typeof links.login_hero === 'object' ? links.login_hero : {};
  const lineWhite = String(hero.line_white || branding?.login_line_white || '').trim();
  const lineY1 = String(hero.line_yellow_1 || branding?.login_line_yellow_1 || '').trim();
  const lineY2 = String(hero.line_yellow_2 || branding?.login_line_yellow_2 || '').trim();
  const heroSubtitle = String(hero.subtitle || '').trim();
  const welcome = String(branding?.welcome_message || '').trim();
  const hasCustomHero = Boolean(lineWhite || lineY1 || lineY2 || heroSubtitle);
  // Sedes suelen heredar "Bienvenido al ciclo D28D…" del padre; no usarlo si ya hay hero propio.
  const inheritedPlatformWelcome = /ciclo\s*d28d|bienvenido al ciclo d28d/i.test(welcome);
  let subtitle = heroSubtitle;
  if (!subtitle && welcome) {
    if (inheritedPlatformWelcome && hasCustomHero) subtitle = '';
    else if (inheritedPlatformWelcome && branding?.brand_slug && !/^d28d/i.test(String(branding.brand_slug))) {
      subtitle = '';
    } else {
      subtitle = welcome;
    }
  }
  return {
    brand_name: String(branding?.brand_name || branding?.nombre || '').trim(),
    line_white: lineWhite,
    line_yellow_1: lineY1,
    line_yellow_2: lineY2,
    subtitle,
    logo_url: String(branding?.logo_url || '').trim(),
    favicon_url: String(branding?.favicon_url || '').trim(),
    brand_slug: String(branding?.brand_slug || '').trim(),
    primary_color: String(branding?.primary_color || '').trim(),
    secondary_color: String(branding?.secondary_color || '').trim(),
  };
}

export function mergeLoginHeroIntoSocialLinks(socialLinks, hero) {
  const base = socialLinks && typeof socialLinks === 'object' ? { ...socialLinks } : {};
  const nextHero = {
    line_white: String(hero?.line_white || '').trim(),
    line_yellow_1: String(hero?.line_yellow_1 || '').trim(),
    line_yellow_2: String(hero?.line_yellow_2 || '').trim(),
  };
  const hasAny = Object.values(nextHero).some(Boolean);
  if (!hasAny) {
    if (base.login_hero) delete base.login_hero;
    return base;
  }
  base.login_hero = nextHero;
  return base;
}
